import { Link } from "react-router-dom";
import type { SpecialTopic } from "@/types";
import { getMovieById } from "@/mocks/movies";

interface SpecialCardProps {
  topic: SpecialTopic;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}

export default function SpecialCard({ topic, index, expanded, onToggle }: SpecialCardProps) {
  const movies = topic.movieIds
    .map((id) => getMovieById(id))
    .filter((movie) => Boolean(movie));

  return (
    <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)]">
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200 md:aspect-auto md:h-full">
          <img
            src={topic.poster}
            alt={`${topic.title} 专题海报 - 棒棒TV`}
            title={`${topic.title} - 棒棒TV影视专题`}
            className="h-full w-full object-cover object-top"
          />
          <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 font-heading text-base font-black text-background-50">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        <div className="min-w-0 p-5 md:p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent-600">
            {topic.subtitle}
          </p>
          <h2 className="mt-1 font-heading text-xl font-black text-foreground-950 md:text-2xl">
            {topic.title}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{topic.intro}</p>

          <div className="mt-4 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
              <i className="ri-lightbulb-flash-line mt-0.5 shrink-0 text-secondary-600"></i>
              <span>
                <span className="font-semibold text-secondary-800">推荐理由：</span>
                {topic.reason}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={onToggle}
            className="mt-4 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            {expanded ? "收起合集" : `查看合集（${movies.length} 部）`}
            <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
        </div>
      </div>

      {expanded && movies.length > 0 && (
        <div className="animate-fade border-t border-background-200 bg-background-100 p-5 md:p-6">
          <p className="mb-4 text-sm font-bold text-foreground-900">合集作品</p>
          <div data-product-shop className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {movies.map((movie) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300"
              >
                <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 封面 - 棒棒TV专题合集`}
                    title={`${movie.title} - 棒棒TV`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <p className="truncate text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {movie.title}
                  </p>
                  <p className="mt-1 truncate text-xs text-foreground-500">
                    {movie.genres.slice(0, 2).join(" · ")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}